import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { apiUrl } from "../config/url";
import Navbar from "./Navbar";

const ItemMaster = () => {
  const user_role = localStorage.getItem("user_role");

  // State Management
  const [ItemMasters, setItemMasters] = useState([]);
  const [filteredItemMasters, setFilteredItemMasters] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [newItemMaster, setNewItemMaster] = useState({
    code: "",
    name: "",
    unit_id: "",
    rate: "",
    item_type_id: "",
  });
  const [editMode, setEditMode] = useState(false);
  const [currentItemMaster, setCurrentItemMaster] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [unitOptions, setUnitOptions] = useState([]);
  const [loading, setLoading] = useState(false); // Loading state

  useEffect(() => {
    fetchItemMasters();
    fetchUnits();
  }, []);

  useEffect(() => {
    const lowercasedSearchTerm = searchTerm.toLowerCase();
    setFilteredItemMasters(
      ItemMasters.filter((item) =>
        item.item_name?.toLowerCase().includes(lowercasedSearchTerm) ||
        item.item_code?.toLowerCase().includes(lowercasedSearchTerm)
      )
    );
  }, [searchTerm, ItemMasters]);

  // Fetch Item Masters
  const fetchItemMasters = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("api_token");
      const response = await axios.get(`${apiUrl}/admin/getAllItems`, {
        headers: { api_token: token },
      });

      if (Array.isArray(response.data.data)) {
        setItemMasters(response.data.data);
        setFilteredItemMasters(response.data.data); // Initialize filtered list
      } else {
        console.error("Expected an array but got:", response.data);
        toast.error("Failed to fetch records");
      }
    } catch (error) {
      console.error("Error fetching item masters", error);
      toast.error("Failed to fetch records");
    } finally {
      setLoading(false);
    }
  };

  // Fetch Units
  const fetchUnits = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("api_token");
      const response = await axios.get(`${apiUrl}/admin/getAllUnits`, {
        headers: { api_token: token },
      });

      setUnitOptions(response.data.data);
    } catch (error) {
      console.error("Error fetching units", error);
      toast.error("Failed to fetch units");
    } finally {
      setLoading(false);
    }
  };

  // Create Item Master
  const createItemMaster = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("api_token");
      const response = await axios.post(`${apiUrl}/admin/createItem`, {
        code: newItemMaster.code,
        name: newItemMaster.name,
        unit_id: newItemMaster.unit_id,
        rate: parseFloat(newItemMaster.rate),
        item_type_id: parseInt(newItemMaster.item_type_id),
      }, {
        headers: { api_token: token },
      });

      const createdItem = {
        item_id: newItemMaster.item_id,
        unit_id: newItemMaster.unit_id,
        item_code: newItemMaster.code,
        item_name: newItemMaster.name,
        item_rate: newItemMaster.rate.toString(),
        item_type_id: newItemMaster.item_type_id,
        unit_name: unitOptions.find(unit => unit.id === newItemMaster.unit_id)?.name || '',
        unit_short: unitOptions.find(unit => unit.id === newItemMaster.unit_id)?.unit_short || '',
      };

      setItemMasters(prev => [...prev, createdItem]);
      setFilteredItemMasters(prev => [...prev, createdItem]);
      resetForm();
      setIsModalOpen(false);
      toast.success("ItemMaster created successfully");
    } catch (error) {
      console.error("Error creating item master", error);
      toast.error("Failed to create item master");
    } finally {
      setLoading(false);
    }
  };

  // Update Item Master
  const updateItemMaster = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("api_token");
      await axios.post(`${apiUrl}/admin/updateItem`, {
        item_id: currentItemMaster.item_id,
        code: newItemMaster.code,
        name: newItemMaster.name,
        unit_id: newItemMaster.unit_id,
        rate: parseFloat(newItemMaster.rate),
        item_type_id: parseInt(newItemMaster.item_type_id),
      }, {
        headers: { api_token: token },
      });

      fetchItemMasters(); // Refresh the list
      resetForm();
      setIsModalOpen(false);
      toast.success("ItemMaster updated successfully");
    } catch (error) {
      console.error("Error updating item master", error);
      toast.error("Failed to update item master");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (ItemMaster) => {
    setCurrentItemMaster(ItemMaster);
    setNewItemMaster({
      code: ItemMaster.item_code || "",
      name: ItemMaster.item_name || "",
      unit_id: ItemMaster.unit_id || "",
      rate: ItemMaster.item_rate || "",
      item_type_id: ItemMaster.item_type_id || "",
    });
    setEditMode(true);
    setIsModalOpen(true);
  };

  const openCreateModal = () => {
    resetForm();
    setEditMode(false);
    const nextCode = ItemMasters.length + 1;
    setNewItemMaster(prev => ({ ...prev, code: nextCode.toString() }));
    setIsModalOpen(true);
  };

  const resetForm = () => {
    setNewItemMaster({
      code: "",
      name: "",
      unit_id: "",
      rate: "",
      item_type_id: "",
    });
    setCurrentItemMaster(null);
    setEditMode(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editMode) {
      updateItemMaster();
    } else {
      createItemMaster();
    }
  };

  return (
    <>
      <Navbar />

      <div>
        <ToastContainer />
        <h1 className="text-3xl font-bold text-center my-6">
          Item Master Management
        </h1>


        {loading ? (
          <div className="text-center">Loading...</div> // Loading Indicator
        ) : (
          <div className="flex justify-center mb-4">
            <table className="bg-white border border-gray-200 table-auto">
              <thead>
                <tr className="border-none">
                  <th colSpan={user_role === "1" ? 7 : 6} className="p-4 border-none bg-white">
                    <div className="flex justify-between items-center">
                      <input
                        type="text"
                        placeholder="Search by Name, Contact or Code"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="p-2 border border-gray-300 rounded w-1/2"
                      />
                      {user_role === "1" && (
                        <button
                          onClick={openCreateModal}
                          className="px-4 py-2 text-white bg-blue-600 rounded"
                        >
                          Create itemMaster
                        </button>
                      )}
                    </div>
                  </th>
                </tr>
                <tr>
                  <th className="py-1 px-2 border bg-gray-100 text-gray-800 font-semibold text-left">Sr no</th>
                  <th className="py-1 px-2 border bg-gray-100 text-gray-800 font-semibold text-left">Item Code</th>
                  <th className="py-1 px-2 border bg-gray-100 text-gray-800 font-semibold text-left">Item Name</th>
                  <th className="py-1 px-2 border bg-gray-100 text-gray-800 font-semibold text-left">Unit</th>
                  <th className="py-1 px-2 border bg-gray-100 text-gray-800 font-semibold text-left">Item Type</th>
                  <th className="py-1 px-2 border bg-gray-100 text-gray-800 font-semibold text-left">Rate</th>

                  {user_role === "1" && (
                    <th className="py-1 px-2 border bg-gray-100 text-gray-800 font-semibold text-center">Actions</th>
                  )}
                </tr>

              </thead>
              <tbody>
                {filteredItemMasters.map((ItemMaster, index) => (
                  <tr key={ItemMaster.item_id} className="even:bg-gray-50">
                    <td className="py-1 px-2 border text-gray-800">{index + 1}</td>
                    <td className="py-1 px-2 border text-gray-800">{ItemMaster.item_code}</td>
                    <td className="py-1 px-2 border text-gray-800">{ItemMaster.item_name}</td>
                    <td className="py-1 px-2 border text-gray-800">{ItemMaster.unit_name}</td>
                    <td className="py-1 px-2 border text-gray-800">{ItemMaster.item_type_id === 1 ? "Item" : "Labour"}</td>
                    <td className="py-1 px-2 border text-gray-800">{ItemMaster.item_rate}</td>
                    {user_role === "1" && (
                      <td className="py-1 px-2 border text-center">
                        <button
                          onClick={() => handleEdit(ItemMaster)}
                          className="bg-yellow-500 text-white py-1 px-2 rounded hover:bg-yellow-600"
                        >
                          Edit
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>


        )}

        {/* Modal for Create/Edit */}
        {isModalOpen && (
          <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50">
            <div className="bg-white p-4 rounded shadow-lg w-96">
              <h2 className="text-xl font-bold mb-4">
                {editMode ? "Edit Item Master" : "Create Item Master"}
              </h2>
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label className="block mb-1">Item Code</label>
                  <input
                    type="text"
                    value={newItemMaster.code}
                    onChange={(e) => setNewItemMaster({ ...newItemMaster, code: e.target.value })}
                    className="w-full border border-gray-300 rounded p-2"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block mb-1">Item Name</label>
                  <input
                    type="text"
                    value={newItemMaster.name}
                    onChange={(e) => setNewItemMaster({ ...newItemMaster, name: e.target.value })}
                    className="w-full border border-gray-300 rounded p-2"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block mb-1">Unit</label>
                  <select
                    value={newItemMaster.unit_id}
                    onChange={(e) => setNewItemMaster({ ...newItemMaster, unit_id: e.target.value })}
                    className="w-full border border-gray-300 rounded p-2"
                    required
                  >
                    <option value="">Select Unit</option>
                    {unitOptions.map((unit) => (
                      <option key={unit.id} value={unit.id}>
                        {unit.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block mb-1">Rate</label>
                  <input
                    type="number"
                    value={newItemMaster.rate}
                    onChange={(e) => setNewItemMaster({ ...newItemMaster, rate: e.target.value })}
                    className="w-full border border-gray-300 rounded p-2"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block mb-1">Item Type</label>
                  <select
                    value={newItemMaster.item_type_id}
                    onChange={(e) => setNewItemMaster({ ...newItemMaster, item_type_id: e.target.value })}
                    className="w-full border border-gray-300 rounded p-2"
                    required
                  >
                    <option value="">Select Item Type</option>
                    <option value="1">Item</option>
                    <option value="2">Labour</option>
                  </select>
                </div>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="bg-red-500 text-white py-2 px-4 rounded mr-2"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bg-blue-600 text-white py-2 px-4 rounded"
                  >
                    {editMode ? "Update" : "Create"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ItemMaster;
